/**
 * space_ops_board.ts — THE OPERATIONS BOARD (space campaign slice H, 2026-10-01): a section of the ops panel (one hook
 * in SpaceModule.renderOps, under THE CAMPAIGN). Each operation for this war or the next: its state, the escrow, and
 * for every objective the accepted quantity, what remains, the funded reward, the expiry and why it is blocked (the
 * pure lines in space_ops_lines.ts); the actions a pilot may take (enrol, order an extraction, submit receipts) and, for
 * a guild's court, the funding form (the §5C fixture by default). Tokens only (THE TOKEN LAW); every control carries a
 * `data-k` so the panel's 1.2 s re-render keeps the focus, and the form's values live in a draft here.
 */
import type { DbConnection } from '../module_bindings/index.js';
import { Identity } from 'spacetimedb';
import { OP_TEMPLATES, OP_FIXTURE, TEMPLATE_LABEL, opBeacon, opTotal, type OpAlloc, type OpTemplate } from '@shared/space_operation_rules.js';
import { BATTLE_PERIOD_S, BATTLE_LEN_S, BATTLE_END_S } from '@shared/space_campaign.js';
import { boardLines, type BoardOp, type OpsLine } from './space_ops_lines.js';
import {
  ensureOpsSubs, opsRows, objOf, allocOf, enrolled, receiptsOf, extractionsOf, myCourt, propose, cancelOp, enroll,
  startExtraction, submitMine, hexOf, opsDriverState,
} from './space_ops_client.js';

const TONE: Record<string, string> = {
  none: 'var(--ax-text)', good: 'var(--ax-good)', bad: 'var(--ax-bad)', warn: 'var(--ax-warn)', muted: 'var(--ax-muted)', info: 'var(--ax-info)',
};
function lineEl(l: OpsLine): HTMLDivElement {
  const d = document.createElement('div');
  d.style.cssText = `font:11px var(--ax-mono);color:${TONE[l.tone ?? 'none']};margin:2px 0;display:flex;gap:6px;align-items:baseline;overflow-wrap:anywhere`;
  const ic = document.createElement('span');
  ic.textContent = l.icon;
  ic.setAttribute('aria-hidden', 'true');
  ic.style.cssText = 'flex:0 0 auto;width:1.2em;text-align:center';
  const tx = document.createElement('span');
  tx.textContent = l.text;
  d.append(ic, tx);
  return d;
}
const draft: { alloc: OpAlloc; side: number; when: 'this' | 'next' } = { alloc: { ...OP_FIXTURE }, side: 0, when: 'next' };

export interface BoardHost {
  conn: DbConnection;
  camp: { campaignId: bigint; cyc: bigint; state: string } | null;
  toast(t: string): void;
  rerender(): void;
}

/** The section. `h` is the panel's header builder, `btnCss` its button style. */
export function renderOperationsBoard(root: HTMLElement, host: BoardHost, h: (txt: string) => HTMLElement, btnCss: string): void {
  const { conn } = host;
  ensureOpsSubs(conn);
  root.appendChild(h('OPERATIONS — guild-funded objectives, paid once on verified evidence'));
  const me = conn.identity;
  const meName = me ? (conn.db.account.identity.find(me)?.username ?? '') : '';
  const nowS = Date.now() / 1000;
  const cur = Math.floor(nowS / BATTLE_PERIOD_S);
  const btn = (label: string, k: string, fn: () => void, title = ''): HTMLButtonElement => {
    const b = document.createElement('button');
    b.textContent = label;
    b.dataset.k = k;
    if (title) b.title = title;
    b.style.cssText = btnCss;
    b.onclick = fn;
    return b;
  };
  const rows = opsRows(conn).filter((o) => o.state !== 'settled' || nowS - Number(o.settledAt.microsSinceUnixEpoch / 1_000_000n) < 2 * 3600);
  const rank: Record<string, number> = { active: 0, open: 1, funded: 2, draft: 3, closing: 4, settled: 5 };
  rows.sort((a, b) => (rank[a.state] ?? 9) - (rank[b.state] ?? 9) || (a.opId < b.opId ? 1 : -1));
  if (!rows.length) root.appendChild(lineEl({ icon: '…', tone: 'muted', text: 'No operation stands. A guild\'s court funds one from its treasury: objectives for the scout, the hauler, the engineer and the screen, paid when the war settles.' }));
  const court = myCourt(conn);
  for (const op of rows.slice(0, 6)) {
    const g = conn.db.guild.guildId.find(op.guildId);
    const accepted = Object.fromEntries(OP_TEMPLATES.map((t) => [t, [] as Array<{ slot: string; amount: number }>])) as BoardOp['accepted'];
    for (const r of receiptsOf(conn, op.opId)) accepted[r.template as OpTemplate]?.push({ slot: r.slot, amount: Number(r.amountSov) });
    const taker = Object.fromEntries(OP_TEMPLATES.map((t) => {
      const o = objOf(conn, op.opId, t);
      const who = o && !o.beneficiary.isEqual(Identity.zero()) ? (conn.db.account.identity.find(o.beneficiary)?.username ?? 'a pilot') : '';
      return [t, who];
    })) as BoardOp['taker'];
    const mine = Object.fromEntries(OP_TEMPLATES.map((t) => [t, enrolled(conn, op.opId, t)])) as BoardOp['mine'];
    let meter: BoardOp['meter'] = null;
    try {
      for (const m of conn.db.opMeter.iter()) if (me && m.opId === op.opId && m.identity.isEqual(me)) meter = { kind: m.kind, accMs: m.accMs, reason: m.reason };
    } catch { /* pre-publish */ }
    const ext = extractionsOf(conn).filter((e) => e.opId === op.opId).sort((a, b) => Number(b.updatedAt.microsSinceUnixEpoch - a.updatedAt.microsSinceUnixEpoch))[0];
    const view: BoardOp = {
      opId: op.opId, guildName: g ? `${g.name} <${g.tag}>` : `guild ${op.guildId}`, side: op.side, state: op.state, totalSov: op.totalSov, heldSov: op.heldSov,
      paidSov: op.paidSov, refundSov: op.refundSov, alloc: allocOf(op), cutoffS: Number(op.cutoffAt.microsSinceUnixEpoch / 1_000_000n), settleCause: op.settleCause,
      note: op.note, beaconName: opBeacon(Number(op.cyc), op.opId).name, accepted, taker, mine, meter,
      extraction: ext ? { label: `${op.side === 0 ? 'Concord' : 'Compact'} ${ext.cls} #${ext.slot + 1}`, state: ext.state, arriveInS: ext.arriveS - nowS, dwellMs: ext.dwellMs } : null,
    };
    const card = document.createElement('div');
    card.dataset.op = String(op.opId);
    card.style.cssText = 'border:1px solid var(--ax-line);border-radius:var(--ax-radius);margin:4px 0;padding:4px 6px;background:var(--ax-bg2)';
    for (const l of boardLines(view, nowS, meName)) card.appendChild(lineEl(l));
    const bar = document.createElement('div');
    bar.style.cssText = 'display:flex;flex-wrap:wrap;gap:2px;margin-top:2px';
    const live = op.state === 'funded' || op.state === 'open' || op.state === 'active';
    for (const t of OP_TEMPLATES) {
      const o = objOf(conn, op.opId, t);
      if (!o || o.state !== 'open' || !live || mine[t]) continue;
      if ((t === 'scout' || t === 'haul') && taker[t]) continue;
      bar.appendChild(btn(`Take: ${TEMPLATE_LABEL[t]}`, `op:${op.opId}:take:${t}`, () => { void enroll(conn, op.opId, t, host.toast).then(() => host.rerender()); }, `Enrol in operation #${op.opId}'s ${TEMPLATE_LABEL[t].toLowerCase()}`));
    }
    if (mine.combat && op.state === 'active' && !ext?.state?.match(/extracting|withdrawn/) && op.campaignId) {
      const escorts = [...conn.db.campaignEscort.iter()].filter((e) => e.campaignId === op.campaignId && e.side === op.side && e.alive).sort((a, b) => (a.cls === b.cls ? a.slot - b.slot : a.cls === 'frigate' ? -1 : 1)).slice(0, 3);
      for (const e of escorts) bar.appendChild(btn(`Extract ${e.cls} #${e.slot + 1}`, `op:${op.opId}:ext:${e.cls}${e.slot}`, () => { void startExtraction(conn, op.opId, e.cls, e.slot, host.toast).then(() => host.rerender()); }, 'Order this escort out on a declared course to your side\'s refuge'));
    }
    if ((mine.haul || mine.repair) && (live || op.state === 'closing')) bar.appendChild(btn('Submit my receipts', `op:${op.opId}:submit`, () => { void submitMine(conn, op.opId, host.toast).then(() => host.rerender()); }, 'Your landed runs and eligible patches (the sweep also collects them every 10 s)'));
    if (court && court.guildId === op.guildId && op.state !== 'settled' && op.state !== 'closing') bar.appendChild(btn('Cancel (court motion)', `op:${op.opId}:cancel`, () => { void cancelOp(conn, op.opId, host.toast).then(() => host.rerender()); }, 'Pays completed eligible work, refunds the rest'));
    if (bar.childElementCount) card.appendChild(bar);
    root.appendChild(card);
  }
  const ds = opsDriverState();
  if (ds.lastError && Date.now() - ds.lastAt < 8000) root.appendChild(lineEl({ icon: '⚠', tone: 'warn', text: `Last ${ds.lastKind === 'dwell' ? 'dwell' : 'verification'} beat refused: ${ds.lastError}` }));
  // ── the court's funding form ──
  if (!court) return;
  const form = document.createElement('div');
  form.style.cssText = 'border:1px dashed var(--ax-line2);border-radius:var(--ax-radius);margin:6px 0;padding:4px 6px';
  form.appendChild(lineEl({ icon: '⚙', tone: 'info', text: `Fund an operation from ${court.name}'s treasury (a court motion — a court of one carries it at once):` }));
  const rowEl = document.createElement('div');
  rowEl.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;align-items:center;font:11px var(--ax-mono);color:var(--ax-soft)';
  for (const t of OP_TEMPLATES) {
    const lab = document.createElement('label');
    lab.textContent = `${TEMPLATE_LABEL[t]} `;
    const inp = document.createElement('input');
    inp.type = 'number'; inp.min = '0'; inp.step = '1';
    inp.value = String(draft.alloc[t]);
    inp.dataset.k = `opform:${t}`;
    inp.style.cssText = 'width:64px;background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);font:11px var(--ax-mono);padding:2px 4px';
    inp.addEventListener('input', () => { draft.alloc[t] = Math.max(0, Math.floor(Number(inp.value) || 0)); });
    lab.appendChild(inp);
    rowEl.appendChild(lab);
  }
  form.appendChild(rowEl);
  const choose = document.createElement('div');
  choose.style.cssText = 'display:flex;flex-wrap:wrap;gap:2px;margin-top:3px';
  const thisOpen = (nowS - cur * BATTLE_PERIOD_S) < BATTLE_LEN_S - BATTLE_END_S;
  for (const [side, label] of [[0, 'for the Concord'], [1, 'for the Compact']] as const) {
    const b = btn(`${draft.side === side ? '● ' : '○ '}${label}`, `opform:side${side}`, () => { draft.side = side; host.rerender(); });
    choose.appendChild(b);
  }
  for (const [when, label] of [['this', thisOpen ? 'this hour\'s war' : 'this hour (closed)'], ['next', 'the next war']] as const) {
    const b = btn(`${draft.when === when ? '● ' : '○ '}${label}`, `opform:${when}`, () => { draft.when = when; host.rerender(); });
    choose.appendChild(b);
  }
  form.appendChild(choose);
  const total = opTotal(draft.alloc);
  const cyc = BigInt(draft.when === 'this' ? cur : cur + 1);
  form.appendChild(btn(`Propose — ${total} ⓒ (cycle ${cyc})`, 'opform:propose', () => {
    void propose(conn, { guildId: court.guildId, side: draft.side, cyc, ...draft.alloc }, host.toast).then(() => host.rerender());
  }, 'Lays a funding motion before your guild\'s court; the treasury pays the escrow when it carries'));
  root.appendChild(form);
  void hexOf; void host.camp;
}
